import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ModalState, UnitForm, UnitOfMeasure } from '../types/system-parameters.types';

interface UnitModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: UnitForm, isEdit?: boolean, itemId?: string) => Promise<void>;
}

export const UnitModal: React.FC<UnitModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<UnitForm>({
    name: '',
    displayName: '',
    category: '',
    symbol: '',
    sortOrder: 1
  });
  const [loading, setLoading] = useState(false);

  // Resetear formulario cuando se abra en modo crear o cuando cambie el item
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const unit = modalState.item as UnitOfMeasure;
        setFormData({
          name: unit.additionalData.name,
          displayName: unit.additionalData.displayName,
          category: unit.additionalData.category,
          symbol: unit.additionalData.symbol,
          sortOrder: unit.additionalData.sortOrder
        });
      } else {
        setFormData({
          name: '',
          displayName: '',
          category: '',
          symbol: '',
          sortOrder: 1
        });
      }
    }
  }, [modalState.isOpen, modalState.mode, modalState.item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.displayName.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(
        formData,
        modalState.mode === 'edit',
        modalState.item?.id
      );
      onClose();
    } catch (error) {
      console.error('Error al guardar unidad de medida:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {modalState.mode === 'edit' ? 'Editar Unidad de Medida' : 'Nueva Unidad de Medida'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: kg"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Nombre Display *</Label>
            <Input
              id="displayName"
              value={formData.displayName}
              onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              placeholder="Ej: Kilogramo"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="symbol">Simbolo</Label>
            <Input
              id="symbol"
              value={formData.symbol}
              onChange={(e) => setFormData(prev => ({ ...prev, symbol: e.target.value }))}
              placeholder="Ej: kg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Categoria</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="peso">Peso</SelectItem>
                <SelectItem value="volumen">Volumen</SelectItem>
                <SelectItem value="cantidad">Cantidad</SelectItem>
                <SelectItem value="dimension">Dimension</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sortOrder">Orden</Label>
            <Input
              id="sortOrder"
              type="number"
              min={1}
              value={formData.sortOrder}
              onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 1 }))}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim() || !formData.displayName.trim() || loading}
              className="bg-macartur-primary"
            >
              {loading ? 'Guardando...' : modalState.mode === 'edit' ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
